import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {List} from '@Components/list';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {getSelectedListItems} from '@Components/list/list-reducer';
import {disableNextButton, enableNextButton, updatePinterestPublishingParamsBoardId} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {createPinterestBoard, getPinterestBoards} from '@Libraries/social-media-wizard-accounts-library';
import type {PinterestBoard} from '@Components/social-media-wizard/social-media-wizard.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {ErrorBox} from '@Components/error-box';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {InputField} from '@Components/input-field-v2';
import {Text, TextSize} from '@Components/text';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {Button, Size, Type} from '@Components/button';
import {getAssetUrl} from '@Utils/s3.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './pinterest-board-id.module.scss';

interface PinterestBoardIdProps {
  account: SocialAccount;
}

export function PinterestBoardId({...props}: PinterestBoardIdProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const LIST_ID = `pinterest-boards-${props.account.id}`;
  const rootState = useAppSelector((state) => {
    return state;
  });
  const selectedListItems = getSelectedListItems(rootState.list, LIST_ID);
  const [pinterestBoards, setPinterestBoards] = useState<PinterestBoard[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEmptyError, setIsEmptyError] = useState<boolean>(false);
  const selectedBoardId = selectedListItems?.[0]?.id?.split('-')?.pop() ?? null;
  const [selectionName, setSelectionName] = useState<string>('');
  const [doPinterestBoardsNotExist, setDoPinterestBoardsNotExist] = useState<boolean>(false);
  const [newBoardName, setNewBoardName] = useState<InputType>('');
  const [isCreatingPinterestBoard, setIsCreatingPinterestBoard] = useState<boolean>(false);
  const [isBoardCreationError, setIsBoardCreationError] = useState<boolean>(false);
  const [isBoardFetchingError, setIsBoardFetchingError] = useState<boolean>(false);

  const updateBoardId = (boardId: string): void => {
    dispatch(updatePinterestPublishingParamsBoardId({boardId, idSocialMediaAccount: props.account.id}));
  };

  const onCreateBoard = (): void => {
    void createNewPinterestBoard();
  };

  const getBoardCreationButtonText = (): string => {
    if (isBoardCreationError) {
      return window.i18next.t('pmwjs_retry');
    }

    return window.i18next.t('pmwjs_create');
  };

  const getBoardCreationButtonType = (): Type => {
    if (isBoardCreationError) {
      return Type.SECONDARY;
    }

    return Type.PRIMARY;
  };

  const getBoardsIdsList = (): ReactElement<any> => {
    if (doPinterestBoardsNotExist) {
      return getBoardCreationForm();
    }

    return getBoardsList();
  };

  const getListItemByBoardName = (board: PinterestBoard, index: number): ListItemStorage => {
    const graphicBackdropURL = board.media.imageCoverUrl ?? getAssetUrl('pinterest-boards-backdrop.webp');
    const itemId = `board-id-${board.id}`;
    const isLastItem = index === pinterestBoards.length - 1;
    const isFirstItem = index === 0;
    return {
      subtext: '',
      id: itemId,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: board.name,
      isSelected: isFirstItem,
      thumbnailClassName: styles.thumbnail,
      isItemUnmargined: isLastItem,
      thumbnail: {
        type: ThumbnailType.IMAGE,
        imageSrc: graphicBackdropURL,
      },
    };
  };

  const loadPinterestBoards = async (): Promise<void> => {
    try {
      const result = await getPinterestBoards(props.account.id);

      if (!result.length) {
        setDoPinterestBoardsNotExist(true);
      } else {
        setPinterestBoards(result);
        setSelectionName(result[0].name);
        updateBoardId(result[0].id);
        setDoPinterestBoardsNotExist(false);
        dispatch(enableNextButton());
      }
    } catch (e) {
      setIsBoardFetchingError(true);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewPinterestBoard = async (): Promise<void> => {
    setIsCreatingPinterestBoard(true);
    setIsBoardCreationError(false);
    try {
      const result = await createPinterestBoard(props.account.id, newBoardName as string);

      if (result.id) {
        setPinterestBoards([result]);
        setSelectionName(result.name);
        updateBoardId(result.id);
        setDoPinterestBoardsNotExist(false);
        dispatch(enableNextButton());
      } else {
        setIsBoardCreationError(true);
      }
    } catch (error) {
      console.error(error);
      setIsBoardCreationError(true);
    } finally {
      setIsCreatingPinterestBoard(false);
    }
  };

  useEffect(() => {
    void loadPinterestBoards();
    dispatch(disableNextButton());
  }, []);

  useDidUpdateEffect(() => {
    if (!selectedBoardId) {
      setIsEmptyError(true);
      setSelectionName('');
      return;
    }

    setIsEmptyError(false);
    updateBoardId(selectedBoardId);
    setSelectionName(
      pinterestBoards.find((board) => {
        return board.id === selectedBoardId;
      })?.name ?? ''
    );
  }, [selectedBoardId]);

  const getListItems = (): ListItemStorage[] => {
    return [
      ...pinterestBoards.map((board, index) => {
        return getListItemByBoardName(board, index);
      }),
    ];
  };

  const getBoardCreationErrorMessage = (): ReactElement<any> | null => {
    if (!isBoardCreationError) {
      return null;
    }

    return <ErrorBox title="" description={window.i18next.t('pmwjs_pinterest_board_board_creation_error')} isPlain descriptionSize={TextSize.XXSMALL} />;
  };

  const getDropDownSelectorText = (): string => {
    if (isLoading) {
      return window.i18next.t('pmwjs_loading_boards');
    }

    if (selectionName === '') {
      return window.i18next.t('pmwjs_select_a_board');
    }

    return selectionName;
  };

  const getBoardIdsButton = (): ReactElement<any> => {
    return (
      <DropdownDefaultSelectBox
        id={`pinterest-board-selector-${props.account.id}`}
        icon="icon-pin"
        size={DROPDOWN_SIZE.SMALL}
        text={getDropDownSelectorText()}
        className={`${isEmptyError ? styles.unselected : ''} ${isLoading ? styles.cursorDisallowed : ''} _unmargin`}
        isLoading={isLoading}
        textClassName={isLoading ? styles.loadingText : ''}
        isDisabled={isBoardFetchingError}
      />
    );
  };

  const getBoardCreationForm = (): ReactElement<any> => {
    return (
      <div className="spacing-p-0">
        <div>
          <Text val={window.i18next.t('pmwjs_create_a_board')} size={TextSize.XXSMALL} className="spacing-m-l-1" />
        </div>
        <InputField
          placeholder={window.i18next.t('pmwjs_pinterest_board_placeholder')}
          input={newBoardName}
          onInputChange={setNewBoardName}
          onInputSubmit={onCreateBoard}
          isDisabled={isCreatingPinterestBoard}
          inputClassName={isBoardCreationError ? styles.unselected : ''}
        />
        {getBoardCreationErrorMessage()}
        <Button
          text={getBoardCreationButtonText()}
          customClasses="spacing-m-t-2"
          isFullWidth
          size={Size.SMALL}
          type={getBoardCreationButtonType()}
          disabled={!newBoardName}
          isLoading={isCreatingPinterestBoard}
          onClick={onCreateBoard}
        />
      </div>
    );
  };

  const getBoardsList = (): ReactElement<any> => {
    return <List id={LIST_ID} className={styles.unmargin} multiSelect={false} items={getListItems()} />;
  };
  const getErrorMessage = (): ReactElement<any> | null => {
    if (!isEmptyError && !isBoardFetchingError) {
      return null;
    }

    let errorMessage = window.i18next.t('pmwjs_please_select_a_board');

    if (isBoardFetchingError) {
      errorMessage = window.i18next.t('pmwjs_board_loading_error');
    }

    return <ErrorBox title="" description={errorMessage} isPlain />;
  };

  return (
    <>
      <Dropdown
        id={`pinterest-social-posts-board-${props.account.id}`}
        className="pinterest-board-dropdown"
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        selector={getBoardIdsButton()}
        popup={getBoardsIdsList()}
        popupClassName={styles.dropdown}
        popUpHasCustomWidth
        doCloseOnInnerClick={false}
      />
      {getErrorMessage()}
    </>
  );
}
